import { round2 } from "./pricing";

/**
 * Programa de pontos (Ultra, 05/10/2026). UMA conta, usada pelo servidor (criação do pedido e crédito no
 * Entregue) e pelo link/PDV (prévia): quanto um pedido rende e quanto cada prêmio desconta.
 *
 * Ordem do desconto: o prêmio entra DEPOIS do cupom (sobre os produtos e a entrega que sobraram) e antes do
 * acréscimo de cartão — o chamador usa netProducts/netDelivery daqui para o acréscimo e o total.
 */
export type LoyaltyRewardType = "fixed" | "percent" | "free_delivery" | "gift" | "cashback";

export interface LoyaltyReward {
  id: string;
  name: string;
  type: LoyaltyRewardType;
  /** Pontos que o prêmio custa. Cashback: mínimo de pontos por uso (0 = qualquer quantidade). */
  cost: number;
  /** fixed: R$ · percent: % · cashback: R$ por ponto · free_delivery e gift: 0. */
  value: number;
  /** Unidades disponíveis (null = sem limite). Conta os resgates feitos desde `stockSetAt`. */
  stock: number | null;
  stockSetAt: string | null;
  active: boolean;
}

export interface LoyaltyEarnWay {
  id: string;
  name: string;
  points: number;
}

export interface LoyaltyConfig {
  earnMode: "real" | "pedido";
  /** Modo "real": pontos a cada `realStep` reais ("1 ponto a cada R$ 15"). O nome ficou do tempo em que o passo era R$ 1. */
  pointsPerReal: number;
  /** Modo "real": o "a cada R$ X" (R$ 1 a R$ 10.000; 1 = o "R$ 1 = N pontos" de antes). */
  realStep: number;
  /** Pontos por pedido (modo "pedido"). */
  pointsPerOrder: number;
  /** Pedido mínimo (produtos) para pontuar; 0 = sem mínimo. Vale para os bônus também. */
  minOrder: number;
  /** Validade de cada lote, em dias; null = não vence. */
  validityDays: number | null;
  rewards: LoyaltyReward[];
  /** "Outras formas de ganhar": aparecem para o cliente e viram atalho no "Dar pontos". */
  earnWays: LoyaltyEarnWay[];
  bonusFirstOrder: number;
  bonusBirthday: number;
  /** Dias da semana (0 = domingo) em que o pedido rende em dobro. */
  doubleDays: number[];
  referralPoints: number;
  oneRewardPerOrder: boolean;
  allowWithCoupon: boolean;
}

export const LOYALTY_REWARD_TYPES: LoyaltyRewardType[] = ["fixed", "percent", "free_delivery", "gift", "cashback"];
export const LOYALTY_VALIDITY_OPTIONS: Array<number | null> = [30, 60, 90, 180, 365, null];
/** Janela do bônus de aniversário: pedido até N dias antes ou depois da data. */
export const LOYALTY_BIRTHDAY_WINDOW_DAYS = 3;

export const LOYALTY_DEFAULTS: LoyaltyConfig = {
  earnMode: "real",
  pointsPerReal: 1,
  realStep: 1,
  pointsPerOrder: 1,
  minOrder: 0,
  validityDays: 90,
  rewards: [],
  earnWays: [],
  bonusFirstOrder: 0,
  bonusBirthday: 0,
  doubleDays: [],
  referralPoints: 0,
  oneRewardPerOrder: true,
  allowWithCoupon: false,
};

const int = (v: unknown, min: number, max: number, fallback: number): number => {
  const n = Math.trunc(Number(v));
  return Number.isFinite(n) ? Math.min(Math.max(n, min), max) : fallback;
};
const money = (v: unknown, max: number): number => {
  const n = Number(typeof v === "string" ? v.replace(",", ".") : v);
  return Number.isFinite(n) ? Math.min(Math.max(round2(n), 0), max) : 0;
};
const text = (v: unknown, max: number): string => String(v ?? "").trim().replace(/\s+/g, " ").slice(0, max);

/** Config da loja com os defaults e limites aplicados (servidor grava o resultado; cliente lê igual). */
export function normalizeLoyaltyConfig(raw: unknown): LoyaltyConfig {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const rewards: LoyaltyReward[] = [];
  const seenIds = new Set<string>();
  for (const it of Array.isArray(r["rewards"]) ? (r["rewards"] as unknown[]) : []) {
    const o = (it && typeof it === "object" ? it : {}) as Record<string, unknown>;
    const type = LOYALTY_REWARD_TYPES.includes(o["type"] as LoyaltyRewardType) ? (o["type"] as LoyaltyRewardType) : null;
    const name = text(o["name"], 60);
    const id = text(o["id"], 40);
    if (!type || !name || !id || seenIds.has(id)) continue;
    seenIds.add(id);
    let value = 0;
    if (type === "fixed") value = money(o["value"], 100000);
    else if (type === "percent") value = int(o["value"], 1, 100, 10);
    else if (type === "cashback") {
      const n = Number(String(o["value"] ?? "").replace(",", "."));
      value = Number.isFinite(n) ? Math.min(Math.max(Math.round(n * 10000) / 10000, 0), 1000) : 0;
    }
    if ((type === "fixed" || type === "cashback") && !(value > 0)) continue;
    const stockRaw = o["stock"];
    const stock = stockRaw === null || stockRaw === undefined || stockRaw === "" ? null : int(stockRaw, 0, 1_000_000, 0);
    rewards.push({
      id,
      name,
      type,
      cost: int(o["cost"], type === "cashback" ? 0 : 1, 10_000_000, type === "cashback" ? 0 : 100),
      value,
      stock,
      stockSetAt: typeof o["stockSetAt"] === "string" ? (o["stockSetAt"] as string) : null,
      active: o["active"] !== false,
    });
    if (rewards.length >= 30) break;
  }
  const earnWays: LoyaltyEarnWay[] = [];
  for (const it of Array.isArray(r["earnWays"]) ? (r["earnWays"] as unknown[]) : []) {
    const o = (it && typeof it === "object" ? it : {}) as Record<string, unknown>;
    const name = text(o["name"], 80);
    const id = text(o["id"], 40);
    const points = int(o["points"], 1, 1_000_000, 0);
    if (!name || !id || !points) continue;
    earnWays.push({ id, name, points });
    if (earnWays.length >= 20) break;
  }
  const validityRaw = r["validityDays"];
  const validityDays =
    validityRaw === null ? null : validityRaw === undefined ? LOYALTY_DEFAULTS.validityDays : int(validityRaw, 1, 3650, 90);
  return {
    earnMode: r["earnMode"] === "pedido" ? "pedido" : "real",
    pointsPerReal: int(r["pointsPerReal"], 1, 1000, LOYALTY_DEFAULTS.pointsPerReal),
    realStep: Math.max(1, money(r["realStep"], 10_000)),
    pointsPerOrder: int(r["pointsPerOrder"], 1, 1_000_000, LOYALTY_DEFAULTS.pointsPerOrder),
    minOrder: money(r["minOrder"], 1_000_000),
    validityDays,
    rewards,
    earnWays,
    bonusFirstOrder: int(r["bonusFirstOrder"], 0, 1_000_000, 0),
    bonusBirthday: int(r["bonusBirthday"], 0, 1_000_000, 0),
    doubleDays: Array.from(
      new Set((Array.isArray(r["doubleDays"]) ? (r["doubleDays"] as unknown[]) : []).map((d) => Math.trunc(Number(d)))),
    )
      .filter((d) => d >= 0 && d <= 6)
      .sort((a, b) => a - b),
    referralPoints: int(r["referralPoints"], 0, 1_000_000, 0),
    oneRewardPerOrder: r["oneRewardPerOrder"] !== false,
    allowWithCoupon: r["allowWithCoupon"] === true,
  };
}

/**
 * Pontos que um pedido rende (sem os bônus). `base` = produtos líquidos (sem entrega, já com os descontos);
 * `weekday` = dia da semana do pedido no fuso da loja (0 = domingo).
 *
 * Por real: base × pontos ÷ R$ do passo, para baixo, pedido a pedido (o resto não passa para o próximo). "1 ponto a
 * cada R$ 15": R$ 29,90 → 1, R$ 30 → 2. Com mais de 1 ponto é proporcional: 2 a cada R$ 15 = 1 a cada R$ 7,50 — é o
 * que mantém o "R$ 1 = N pontos" de antes igual (R$ 0,30 a 10 pontos/R$ 1 → 3). Conta em centavos, sem erro de vírgula.
 */
export function pointsForOrder(cfg: LoyaltyConfig, args: { base: number; weekday: number }): number {
  const base = round2(Math.max(0, Number(args.base) || 0));
  if (cfg.minOrder > 0 && base < cfg.minOrder) return 0;
  let p: number;
  if (cfg.earnMode === "pedido") p = cfg.pointsPerOrder;
  else {
    const passo = Math.max(100, Math.round((Number(cfg.realStep) || 1) * 100));
    const n = Math.round(base * 100) * cfg.pointsPerReal;
    p = (n - (n % passo)) / passo;
  }
  if (cfg.doubleDays.includes(args.weekday)) p *= 2;
  return Math.max(0, p);
}

/**
 * Quanto falta no carrinho para o pedido render mais pontos (aviso "faltam R$ X para mais 1 ponto", Pedro 06/10/2026).
 * Só quando o valor faz diferença: "N pontos a cada R$ X" com X acima de R$ 1, ou carrinho abaixo do "Pedido mínimo
 * para pontuar". null no resto: com R$ 1 = N pontos todo real já rende, e por pedido o valor não muda nada.
 * `ganha` = quantos pontos a mais o pedido rende ao chegar lá (já com o dobro do dia).
 */
export function nextLoyaltyStep(cfg: LoyaltyConfig, args: { base: number; weekday: number }): { faltam: number; ganha: number } | null {
  const agora = Math.round(round2(Math.max(0, Number(args.base) || 0)) * 100);
  const minimo = Math.round((cfg.minOrder || 0) * 100);
  const passo = Math.max(100, Math.round((Number(cfg.realStep) || 1) * 100));
  const n = Math.max(1, cfg.pointsPerReal);
  const rende = (centavos: number) => pointsForOrder(cfg, { base: centavos / 100, weekday: args.weekday });
  let alvo: number;
  if (minimo > 0 && agora < minimo) {
    // Abaixo do mínimo não rende nada; por real, o mínimo pode ficar antes do 1º ponto (mínimo R$ 10, 1 a cada R$ 15).
    alvo = cfg.earnMode === "pedido" ? minimo : Math.max(minimo, Math.floor((passo + n - 1) / n));
  } else if (cfg.earnMode === "real" && passo > 100) {
    const k = Math.floor((agora * n) / passo) + 1;
    alvo = Math.floor((k * passo + n - 1) / n);
  } else return null;
  const ganha = rende(alvo) - rende(agora);
  return ganha > 0 ? { faltam: (alvo - agora) / 100, ganha } : null;
}

/** Pedido alcança o mínimo para pontuar (pedido e bônus). */
export function reachesLoyaltyMinimum(cfg: LoyaltyConfig, base: number): boolean {
  return !(cfg.minOrder > 0 && round2(Math.max(0, Number(base) || 0)) < cfg.minOrder);
}

export interface RewardUse {
  rewardId: string;
  name: string;
  type: LoyaltyRewardType;
  value: number;
  /** Pontos gastos neste uso (cashback: os que o cliente escolheu, já ajustados ao que o pedido comporta). */
  points: number;
}

export interface RewardMath {
  productDiscount: number;
  deliveryDiscount: number;
  /** productDiscount + deliveryDiscount — é o que se grava em loyalty_discount. */
  loyaltyDiscount: number;
  netProducts: number;
  netDelivery: number;
  /** Desconto de cada uso, na ordem recebida (brinde = 0). */
  perUse: number[];
  /** Pontos de cada uso depois do ajuste (cashback não gasta mais do que o pedido comporta). */
  pointsPerUse: number[];
  pointsUsed: number;
}

/**
 * Desconto dos prêmios sobre o que sobrou depois do cupom. Fixo/percentual/cashback tiram dos produtos (até zerar);
 * frete grátis zera a entrega que sobrou; brinde não mexe na conta (vira linha de R$ 0,00).
 */
export function computeRewardMath(args: {
  netProducts: number;
  netDelivery: number;
  uses: RewardUse[];
}): RewardMath {
  let products = round2(Math.max(0, Number(args.netProducts) || 0));
  const delivery0 = round2(Math.max(0, Number(args.netDelivery) || 0));
  let delivery = delivery0;
  const startProducts = products;
  const perUse: number[] = [];
  const pointsPerUse: number[] = [];
  for (const u of args.uses) {
    let d = 0;
    let pts = Math.max(0, Math.trunc(Number(u.points) || 0));
    if (u.type === "fixed") {
      d = Math.min(products, round2(u.value));
    } else if (u.type === "percent") {
      d = Math.min(products, round2(startProducts * (Math.min(Math.max(u.value, 0), 100) / 100)));
    } else if (u.type === "cashback") {
      const rate = Number(u.value) || 0;
      if (rate > 0) {
        // Não gasta ponto à toa: no máximo o necessário para zerar os produtos que sobraram.
        const maxPts = Math.ceil(round2(products / rate) - 1e-9);
        pts = Math.min(pts, Math.max(0, maxPts));
        d = Math.min(products, round2(pts * rate));
      } else {
        pts = 0;
      }
    } else if (u.type === "free_delivery") {
      d = delivery;
      delivery = 0;
    }
    if (u.type !== "free_delivery") products = round2(products - d);
    perUse.push(d);
    pointsPerUse.push(pts);
  }
  const productDiscount = round2(startProducts - products);
  const deliveryDiscount = round2(delivery0 - delivery);
  return {
    productDiscount,
    deliveryDiscount,
    loyaltyDiscount: round2(productDiscount + deliveryDiscount),
    netProducts: products,
    netDelivery: delivery,
    perUse,
    pointsPerUse,
    pointsUsed: pointsPerUse.reduce((s, p) => s + p, 0),
  };
}

/** Por que o prêmio não serve agora (null = serve). Mesma checagem na tela e no servidor. */
export function rewardProblem(args: {
  reward: LoyaltyReward;
  balance: number;
  deliveryMode: string | null | undefined;
  feePending?: boolean;
  /** Entrega que sobrou depois do cupom (frete grátis precisa de algo para zerar). */
  netDelivery: number;
  /** Unidades ainda disponíveis (null = sem limite). */
  stockLeft?: number | null;
}): string | null {
  const r = args.reward;
  if (!r.active) return "Este prêmio não está disponível.";
  if (args.stockLeft != null && args.stockLeft <= 0) return "Este prêmio acabou.";
  if (r.type === "cashback") {
    if (args.balance <= 0 || args.balance < Math.max(1, r.cost)) return "Pontos insuficientes.";
    return null;
  }
  if (args.balance < r.cost) return "Pontos insuficientes.";
  if (r.type === "free_delivery") {
    if (args.deliveryMode !== "entrega") return "Frete grátis vale só para entrega.";
    if (args.feePending || !(args.netDelivery > 0)) return "A entrega deste pedido já não tem taxa.";
  }
  return null;
}
